"use client";

import { useQueryClient } from "@tanstack/react-query";
import type { CalendarViewPreferences } from "@workspace/types/src/entities/user";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
} from "@workspace/ui/components/form";
import { Label } from "@workspace/ui/components/label";
import { Switch } from "@workspace/ui/components/switch";
import { Spinner } from "@workspace/ui/components/spinner";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { trpc } from "@/lib/trpc/react";

interface AdvancedSettingsModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialPreferences?: CalendarViewPreferences;
	onPreferencesSaved?: (preferences: CalendarViewPreferences) => void;
}

export const AdvancedSettingsModal = memo<AdvancedSettingsModalProps>(
	({ isOpen, onClose, initialPreferences, onPreferencesSaved }) => {
		const form = useForm<CalendarViewPreferences>({
			defaultValues: {
				show_stage_names: initialPreferences?.show_stage_names ?? false, // Default to showing all stages
				show_set_times: initialPreferences?.show_set_times ?? true,
				compact_view: initialPreferences?.compact_view ?? false,
				highlight_conflicts: initialPreferences?.highlight_conflicts ?? false,
				show_artist_photos: initialPreferences?.show_artist_photos ?? true,
			},
		});

		const queryClient = useQueryClient();

		// tRPC mutation with optimistic updates
		const updateCalendarPreferencesMutation =
			trpc.settings.updateCalendarPreferences.useMutation({
				onMutate: async ({ calendarViewPreferences }) => {

					// Cancel any outgoing refetches
					await queryClient.cancelQueries({
						queryKey: ["calendar-preferences"],
					});

					// Get the previous value
					const previousPreferences = initialPreferences;

					// Optimistically update the parent component immediately
					onPreferencesSaved?.(calendarViewPreferences);

					return { previousPreferences };
				},
				onSuccess: async (data, variables) => {

					// Invalidate and refetch calendar preferences
					await queryClient.invalidateQueries({
						queryKey: ["calendar-preferences"],
					});

					// Force refetch to ensure we have the latest data
					await queryClient.refetchQueries({
						queryKey: ["calendar-preferences"],
					});

					// Get the updated data from cache (the hook uses user?.id from useSupabase)
					// We don't have direct access to user here, so we just wait for the refetch

					// Call the callback again with the saved preferences to ensure consistency
					onPreferencesSaved?.(variables.calendarViewPreferences);

					toast({
						title: "Preferences saved",
						description: "Your calendar view preferences have been updated.",
					});

					// Close modal on success
					onClose();
				},
				onError: (error, variables, context) => {
					console.error("[Modal] Save failed:", error);

					// Rollback optimistic update
					if (context?.previousPreferences) {
						onPreferencesSaved?.(context.previousPreferences);
					}

					toast({
						title: "Error",
						description: "Failed to save preferences. Please try again.",
						variant: "destructive",
					});
				},
			});

		// Reset form when modal opens with new preferences
		useEffect(() => {
			if (isOpen) {
				// Use the actual preferences, including false values
				const resetValues = {
					show_stage_names: initialPreferences?.show_stage_names ?? false,
					show_set_times: initialPreferences?.show_set_times ?? true,
					compact_view: initialPreferences?.compact_view ?? false,
					highlight_conflicts: initialPreferences?.highlight_conflicts ?? false,
					show_artist_photos: initialPreferences?.show_artist_photos ?? true,
				};
				form.reset(resetValues);
			}
		}, [isOpen, initialPreferences, form]);

		const handleSubmit = useCallback(
			(data: CalendarViewPreferences) => {
				updateCalendarPreferencesMutation.mutate({
					calendarViewPreferences: data,
				});
			},
			[updateCalendarPreferencesMutation],
		);

		return (
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md">
					<DialogTitle className="sr-only">
						Calendar Settings
					</DialogTitle>
					<DialogDescription className="sr-only">
						Customize how your calendar view displays filtered content.
					</DialogDescription>
					<DialogHeader className="px-6 pt-6 pb-0">
						<h2 className="font-semibold leading-none tracking-tight">
							Settings
						</h2>
						<p className="text-muted-foreground text-sm">
							Customize your calendar view preferences.
						</p>
					</DialogHeader>

					<div className="px-6 py-6">
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(handleSubmit)}
								className="space-y-6"
							>
								{/* Stage Display Preference */}
								<FormField
									control={form.control}
									name="show_stage_names"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<div className="flex items-center justify-between gap-3">
													<Label
														htmlFor="show-my-stages"
														className="flex flex-col items-start flex-1 gap-1 cursor-pointer"
													>
														<div className="font-semibold">Show my stages only</div>
														<p className="text-sm text-muted-foreground font-normal">
															Only show stages with sets you've saved, rated, or artists you follow
														</p>
													</Label>
													<Switch
														id="show-my-stages"
														checked={field.value ?? false}
														onCheckedChange={field.onChange}
													/>
												</div>
											</FormControl>
										</FormItem>
									)}
								/>

								{/* Show Artist Photos Toggle */}
								<FormField
									control={form.control}
									name="show_artist_photos"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<div className="flex items-center justify-between gap-3">
													<Label
														htmlFor="show-artist-photos"
														className="flex flex-col items-start flex-1 gap-1 cursor-pointer"
													>
														<div className="font-semibold">Show artist photos</div>
														<p className="text-sm text-muted-foreground font-normal">
															Display artist photos in calendar set cards
														</p>
													</Label>
													<Switch
														id="show-artist-photos"
														checked={field.value ?? true}
														onCheckedChange={field.onChange}
													/>
												</div>
											</FormControl>
										</FormItem>
									)}
								/>

								<DialogFooter>
									<div className="flex w-full items-center justify-between">
										<div /> {/* Spacer for alignment */}
										<div className="flex gap-2">
											<Button variant="ghost" onClick={onClose} type="button">
												Cancel
											</Button>
											<Button
												type="submit"
												disabled={updateCalendarPreferencesMutation.isPending}
											>
												{updateCalendarPreferencesMutation.isPending ? (
													<div className="flex items-center gap-2">
														<Spinner size="sm" variant="current" />
														<span>Saving...</span>
													</div>
												) : (
													"Save"
												)}
											</Button>
										</div>
									</div>
								</DialogFooter>
							</form>
						</Form>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);

AdvancedSettingsModal.displayName = "AdvancedSettingsModal";
